import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { contactAPI } from '../../lib/api';
import { publicCmsAPI } from '../../lib/cmsApi';
import { academyPublicAPI } from '../../lib/academyApi';
import { toast } from 'sonner';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle,
  MessageSquare,
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    service_interet: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [trainings, setTrainings] = useState([]);

  useEffect(() => {
    Promise.all([
      publicCmsAPI.getSettings().catch(() => ({ data: null })),
      publicCmsAPI.getServices().catch(() => ({ data: [] })),
      academyPublicAPI.getTrainings().catch(() => ({ data: [] })),
    ]).then(([s, svc, tr]) => {
      setSettings(s.data);
      setServices(svc.data || []);
      setTrainings(tr.data || []);
    });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(formData);
      setSubmitted(true);
      toast.success('Message envoye avec succes!');
      setFormData({ nom: '', email: '', telephone: '', entreprise: '', service_interet: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du message");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: settings?.address || 'Montreal, Quebec, Canada',
    },
    {
      icon: Mail,
      title: 'Courriel',
      content: settings?.email || 'info@peyconsultants.ca',
      href: `mailto:${settings?.email || 'info@peyconsultants.ca'}`,
    },
    ...(settings?.phone ? [{
      icon: Phone,
      title: 'Telephone',
      content: settings.phone,
      href: `tel:${settings.phone}`,
    }] : []),
    {
      icon: Clock,
      title: "Heures d'ouverture",
      content: 'Lundi - Vendredi\n8h00 - 17h00',
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#1D1D1B] py-20 lg:py-24">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="text-[#FFD537] font-semibold mb-4 animate-fadeIn">PARLONS DE VOTRE PROJET</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6 animate-fadeIn">
              Contactez-nous
            </h1>
            <p className="text-xl text-white/70 leading-relaxed animate-fadeIn animation-delay-100">
              Nous sommes a votre ecoute pour discuter de vos projets et repondre a vos questions en gestion des eaux et infrastructures durables.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-app">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl font-bold text-[#1D1D1B] mb-8 section-header">
                Coordonnees
              </h2>
              <div className="space-y-6 mt-10">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                    >
                      <item.icon className="w-5 h-5 text-[#F9B438]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1D1D1B] mb-1">{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="text-neutral-600 hover:text-[#F9B438] transition-colors whitespace-pre-line">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-neutral-600 whitespace-pre-line">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-10 p-6 rounded-xl"
                style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}
              >
                <MessageSquare className="w-8 h-8 text-[#F9B438] mb-3" />
                <h3 className="font-semibold text-[#1D1D1B] mb-2">Reponse rapide</h3>
                <p className="text-neutral-600 text-sm">
                  Nous nous engageons a repondre a toutes les demandes dans un delai de 24 a 48 heures ouvrables.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-[#1D1D1B]">
                    Envoyez-nous un message
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
                      >
                        <CheckCircle className="w-10 h-10 text-[#F9B438]" />
                      </div>
                      <h3 className="font-heading text-2xl font-semibold text-[#1D1D1B] mb-2">
                        Message envoye!
                      </h3>
                      <p className="text-neutral-600 mb-8 max-w-md mx-auto">
                        Merci de nous avoir contactes. Notre equipe vous repondra dans les plus brefs delais.
                      </p>
                      <Button variant="outline" onClick={() => setSubmitted(false)}>
                        Envoyer un autre message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="nom" className="text-[#1D1D1B]">Nom complet *</Label>
                          <Input
                            id="nom"
                            name="nom"
                            value={formData.nom}
                            onChange={handleChange}
                            required
                            placeholder="Votre nom"
                            className="h-11"
                            data-testid="contact-nom"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-[#1D1D1B]">Courriel *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="vous@exemple.com"
                            className="h-11"
                            data-testid="contact-email"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="telephone" className="text-[#1D1D1B]">Telephone</Label>
                          <Input
                            id="telephone"
                            name="telephone"
                            type="tel"
                            value={formData.telephone}
                            onChange={handleChange}
                            placeholder="(514) 555-1234"
                            className="h-11"
                            data-testid="contact-telephone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="entreprise" className="text-[#1D1D1B]">Organisation / Entreprise</Label>
                          <Input
                            id="entreprise"
                            name="entreprise"
                            value={formData.entreprise}
                            onChange={handleChange}
                            placeholder="Nom de votre organisation"
                            className="h-11"
                            data-testid="contact-entreprise"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service_interet" className="text-[#1D1D1B]">Service ou formation d'interet</Label>
                        <select
                          id="service_interet"
                          name="service_interet"
                          value={formData.service_interet}
                          onChange={handleChange}
                          className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm"
                          data-testid="contact-service"
                        >
                          <option value="">-- Selectionner un service ou une formation --</option>
                          {services.length > 0 && (
                            <optgroup label="Nos services">
                              {services.map(s => (
                                <option key={`svc-${s.id}`} value={`Service: ${s.title}`}>{s.title}</option>
                              ))}
                            </optgroup>
                          )}
                          {trainings.length > 0 && (
                            <optgroup label="Formations PEY aCADemie">
                              {trainings.map(t => (
                                <option key={`tr-${t.id}`} value={`Formation: ${t.title}`}>
                                  {t.title} {t.price > 0 ? `(${t.promo_price || t.price} $)` : '(Gratuit)'}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Autre">
                            <option value="Demande generale">Demande generale</option>
                            <option value="Formation sur mesure">Formation sur mesure (entreprise)</option>
                            <option value="Partenariat">Partenariat</option>
                          </optgroup>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#1D1D1B]">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Decrivez votre projet ou vos besoins..."
                          rows={6}
                          data-testid="contact-message"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-12"
                        disabled={loading}
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Envoyer le message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section
        className="h-[300px] relative"
        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
            >
              <MapPin className="w-8 h-8 text-[#1D1D1B]" />
            </div>
            <p className="text-[#1D1D1B] font-heading font-semibold text-lg">
              {settings?.address || 'Montreal, Quebec, Canada'}
            </p>
            <p className="text-neutral-600">Societe de genie-conseil basee au Quebec</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1D1D1B] py-12">
        <div className="container-app text-center">
          <p className="text-white/60">
            Deja client? <a href="/connexion" className="text-[#FFD537] hover:underline font-medium">Accedez a votre espace client</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
